import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { useStep } from '@repo/ui/stepper';
import { Textarea } from '@repo/ui/textarea';
import type { UseFormReturn } from 'react-hook-form';
import z from 'zod';

export const ZOrganizationInfoFormSchema = z.object({
  name: z.string().min(1, t('auth.organizationNameIsRequired')).max(255),
  country: z.string().optional(),
  description: z.string().max(2000).optional(),
});

type Props = {
  form: UseFormReturn<z.infer<typeof ZOrganizationInfoFormSchema>>;
  onNext: () => void;
};

export const CreateOrganizationInformationForm = ({ form, onNext }: Props) => {
  useLocale();

  const { previousStep } = useStep();

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(() => onNext())}>
        <fieldset className="space-y-5" disabled={form.formState.isSubmitting}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.organizationName')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('auth.acmeLtd')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => {
                const { value, ...others } = field;
                return (
                  <FormItem>
                    <FormLabel>
                      {t('auth.country')}{' '}
                      <span className="font-normal text-muted-foreground">
                        {t('auth.optional')}
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('auth.eGUnitedKingdom')}
                        value={value ?? undefined}
                        {...others}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => {
              const { value, ...others } = field;
              return (
                <FormItem>
                  <FormLabel>
                    {t('auth.description')}{' '}
                    <span className="font-normal text-muted-foreground">{t('auth.optional')}</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[80px] resize-y"
                      placeholder={t('auth.briefDescriptionOfTheOrganization')}
                      rows={2}
                      value={value ?? undefined}
                      {...others}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </fieldset>
        <div className="flex justify-between pt-4">
          <Button onClick={() => previousStep()} type="button" variant="outline">
            {t('auth.back')}{' '}
          </Button>
          <Button type="submit">{t('auth.continue')}</Button>
        </div>
      </form>
    </Form>
  );
};
